// Seed data for the demo. Two example contracts: one 小项目, one 大项目
// matching the spec's §5/§6 example (¥100k, 6 milestones, 3 payments, 6
// releases). Both demonstrate the model end-to-end.

import type {
  ContractV2Bundle,
  Milestone,
  PaymentPlanEntry,
  ReleasePlanEntry,
} from './mockTypes';

const NOW = 1780540800000; // fixed reference timestamp so demo is deterministic
const day = 24 * 3600 * 1000;

const mockClient = {
  userId: 'demo_client_001',
  nickname: '北方熊广告',
  accountType: 'company' as const,
};
const mockCreator = {
  userId: 'demo_creator_001',
  nickname: '林小川',
  accountType: 'individual' as const,
};

// =====================================================================
// 小项目 example: ¥800 logo design, full upfront, single delivery
// =====================================================================

const smallContractId = 'demo_contract_small_001';
const smallMilestone: Milestone = {
  id: 'mile_small_001',
  contractId: smallContractId,
  sequenceIndex: 0,
  title: '交付最终成品',
  description: '品牌 LOGO 1 套，配色 + 黑白 + 白色版本，源文件',
  percent: 100, amount: 800,
  dueAt: NOW + 7 * day,
  acceptanceCriteria: '提供 .ai 源文件、PNG 高清版、品牌使用规范 PDF',
  revisionLimit: 2,
  revisionCountUsed: 0,
  requiredAssets: [
    { type: 'source_file', label: '.ai 源文件' },
    { type: 'commercial_license', label: '商用授权书' },
  ],
  submittedAssets: [],
  revisionHistory: [],
  status: 'IN_PROGRESS',
  paymentStatus: 'FUNDED',
};

const smallPayment: PaymentPlanEntry = {
  id: 'pay_small_001',
  contractId: smallContractId,
  sequenceIndex: 0,
  amount: 800,
  coversMilestoneIds: ['mile_small_001'],
  coversTail: false,
  trigger: 'CONTRACT_SIGNED',
  status: 'PLATFORM_CONFIRMED',
  paidAt: NOW - 1 * day,
  platformConfirmedAt: NOW - 1 * day,
  isFinalTail: false,
};

const smallRelease: ReleasePlanEntry = {
  id: 'rel_small_001',
  contractId: smallContractId,
  sequenceIndex: 0,
  milestoneId: 'mile_small_001',
  amount: 800,
  status: 'PENDING',
};

// =====================================================================
// 大项目 example: ¥100,000 short film, 6 milestones / 3 payments / 6 releases
// =====================================================================

const largeContractId = 'demo_contract_large_001';

const largeMilestones: Milestone[] = [
  {
    id: 'mile_L1', contractId: largeContractId, sequenceIndex: 0,
    title: 'M1: 方案与脚本', description: '剧本初稿 + 风格参考板',
    percent: 10, amount: 10000, dueAt: NOW + 5 * day,
    acceptanceCriteria: '剧本满足片长要求，风格参考与简报一致',
    revisionLimit: 2, revisionCountUsed: 0,
    requiredAssets: [{ type: 'other', label: '剧本 PDF' }, { type: 'other', label: '参考板 PDF' }],
    submittedAssets: [
      { type: 'other', label: '剧本 PDF', uploadedAt: NOW - 2 * day, fileUrl: '#' },
      { type: 'other', label: '参考板 PDF', uploadedAt: NOW - 2 * day, fileUrl: '#' },
    ],
    lastDeliveryAt: NOW - 2 * day,
    validityCheckPassed: true,
    revisionHistory: [],
    status: 'COMPLETED', paymentStatus: 'RELEASED',
  },
  {
    id: 'mile_L2', contractId: largeContractId, sequenceIndex: 1,
    title: 'M2: 分镜与动作设计', description: '完整分镜 + 关键动作示意',
    percent: 20, amount: 20000, dueAt: NOW + 12 * day,
    acceptanceCriteria: '所有镜头有分镜稿，关键动作设计合理',
    revisionLimit: 2, revisionCountUsed: 2,
    requiredAssets: [{ type: 'other', label: '分镜 PDF' }],
    submittedAssets: [
      { type: 'other', label: '分镜 PDF', uploadedAt: NOW + 1 * day, fileUrl: '#' },
    ],
    lastDeliveryAt: NOW + 1 * day,
    validityCheckPassed: true,
    revisionHistory: [
      // Seed history so reviewers can see the toggle in action without filing
      // their own. Latest entry is unresolved; earlier one resolved by re-submit.
      {
        id: 'rev_seed_1',
        requestedAt: NOW - 3 * day,
        reason: '分镜稿第 12 个镜头与脚本动作不一致，请按脚本调整',
        returnDueAt: NOW - 1 * day,
        resolvedAt: NOW - 1 * day,
      },
      {
        id: 'rev_seed_2',
        requestedAt: NOW + 1.5 * day,
        reason: '部分关键动作示意还是太抽象，请补充关键帧；另外 P3-P5 的镜头时长需要标注。',
        returnDueAt: NOW + 5 * day,
      },
    ],
    status: 'IN_ACCEPTANCE', paymentStatus: 'FUNDED',
    acceptancePeriodEndsAt: NOW + 1 * day + 9 * day, // company buyer => 7+2 = 9d
  },
  {
    id: 'mile_L3', contractId: largeContractId, sequenceIndex: 2,
    title: 'M3: 角色与场景模型', description: '主要角色 3 个、场景 2 个的初版模型',
    percent: 20, amount: 20000, dueAt: NOW + 25 * day,
    acceptanceCriteria: '模型符合分镜要求，可用于动画',
    revisionLimit: 2, revisionCountUsed: 0,
    requiredAssets: [{ type: 'model_file', label: '模型文件' }],
    submittedAssets: [],
    revisionHistory: [],
    status: 'IN_PROGRESS', paymentStatus: 'FUNDED',
  },
  {
    id: 'mile_L4', contractId: largeContractId, sequenceIndex: 3,
    title: 'M4: 动画段落 1', description: '前 1/3 镜头完成动画',
    percent: 10, amount: 10000, dueAt: NOW + 40 * day,
    acceptanceCriteria: '动画流畅，与分镜一致',
    revisionLimit: 2, revisionCountUsed: 0,
    requiredAssets: [{ type: 'engineering_file', label: '工程文件' }],
    submittedAssets: [],
    revisionHistory: [],
    status: 'NOT_STARTED', paymentStatus: 'UNFUNDED',
  },
  {
    id: 'mile_L5', contractId: largeContractId, sequenceIndex: 4,
    title: 'M5: 动画段落 2', description: '剩余镜头完成动画',
    percent: 10, amount: 10000, dueAt: NOW + 55 * day,
    acceptanceCriteria: '动画流畅，与分镜一致',
    revisionLimit: 2, revisionCountUsed: 0,
    requiredAssets: [{ type: 'engineering_file', label: '工程文件' }],
    submittedAssets: [],
    revisionHistory: [],
    status: 'NOT_STARTED', paymentStatus: 'UNFUNDED',
  },
  {
    id: 'mile_L6', contractId: largeContractId, sequenceIndex: 5,
    title: 'M6: 最终交付', description: '调色 + 配乐 + 最终成片 + 资产移交',
    percent: 10, amount: 10000, dueAt: NOW + 65 * day,
    acceptanceCriteria: '成片可商用，资产齐全',
    revisionLimit: 2, revisionCountUsed: 0,
    requiredAssets: [
      { type: 'source_file', label: '源文件' },
      { type: 'no_watermark', label: '无水印高清成片' },
      { type: 'commercial_license', label: '商用授权书' },
    ],
    submittedAssets: [],
    revisionHistory: [],
    status: 'NOT_STARTED', paymentStatus: 'UNFUNDED',
  },
];

// Three checkpoints: signing covers M1+M2 (10+20 = 30%), after M2 verified
// covers M3+M4+M5+M6 (20+10+10+10 = 50%), final tail = 20%.
const largePayments: PaymentPlanEntry[] = [
  {
    id: 'pay_L1', contractId: largeContractId, sequenceIndex: 0,
    amount: 30000,
    coversMilestoneIds: ['mile_L1', 'mile_L2'],
    coversTail: false,
    trigger: 'CONTRACT_SIGNED',
    status: 'PLATFORM_CONFIRMED', paidAt: NOW - 5 * day, platformConfirmedAt: NOW - 5 * day,
    isFinalTail: false,
  },
  {
    id: 'pay_L2', contractId: largeContractId, sequenceIndex: 1,
    amount: 50000,
    coversMilestoneIds: ['mile_L3', 'mile_L4', 'mile_L5', 'mile_L6'],
    coversTail: false,
    trigger: 'MILESTONE_PASSED', triggerMilestoneId: 'mile_L2',
    status: 'PENDING',
    isFinalTail: false,
  },
  {
    id: 'pay_L3', contractId: largeContractId, sequenceIndex: 2,
    amount: 20000,
    coversMilestoneIds: [],
    coversTail: true,
    trigger: 'FINAL_TAIL', triggerMilestoneId: 'mile_L6',
    status: 'PENDING',
    isFinalTail: true,
  },
];

// Release plan = milestones at their authored percent + tail at the end.
// Tail releases only after final asset handover; here it's just rendered.
const largeReleases: ReleasePlanEntry[] = [
  { id: 'rel_L1', contractId: largeContractId, sequenceIndex: 0, milestoneId: 'mile_L1', amount: 10000, status: 'RELEASED', releasedAt: NOW - 1 * day },
  { id: 'rel_L2', contractId: largeContractId, sequenceIndex: 1, milestoneId: 'mile_L2', amount: 20000, status: 'PENDING' },
  { id: 'rel_L3', contractId: largeContractId, sequenceIndex: 2, milestoneId: 'mile_L3', amount: 20000, status: 'PENDING' },
  { id: 'rel_L4', contractId: largeContractId, sequenceIndex: 3, milestoneId: 'mile_L4', amount: 10000, status: 'PENDING' },
  { id: 'rel_L5', contractId: largeContractId, sequenceIndex: 4, milestoneId: 'mile_L5', amount: 10000, status: 'PENDING' },
  { id: 'rel_L6', contractId: largeContractId, sequenceIndex: 5, milestoneId: 'mile_L6', amount: 10000, status: 'PENDING' },
];

export const seedBundles: ContractV2Bundle[] = [
  {
    contract: {
      id: smallContractId,
      initiatorRole: 'client',
      client: mockClient,
      creator: mockCreator,
      title: '品牌 LOGO 设计',
      description: '为新品牌设计一套 LOGO，含配色 + 黑白版本',
      projectType: 'small',
      totalAmount: 800,
      tailPercent: 0, tailAmount: 0,
      totalPaid: 800, totalReleased: 0, totalRefunded: 0,
      acceptanceState: 'active',
      createdAt: NOW - 2 * day, signedAt: NOW - 1 * day,
      accountTypeAtSign: 'company',
      revisionLimitDefault: 2,
      requiresInvoice: false,
    },
    milestones: [smallMilestone],
    paymentPlan: [smallPayment],
    releasePlan: [smallRelease],
    changes: [], disputes: [],
  },
  {
    contract: {
      id: largeContractId,
      initiatorRole: 'creator',
      client: mockClient,
      creator: mockCreator,
      title: '品牌短片 60s 全流程制作',
      description: '从剧本到成片的全流程，适用于线下大屏 + 社交媒体',
      projectType: 'large',
      totalAmount: 100000,
      tailPercent: 20, tailAmount: 20000,
      totalPaid: 30000,    // pay_L1 only; pay_L2 hasn't fired yet
      totalReleased: 10000, // M1 released
      totalRefunded: 0,
      acceptanceState: 'active',
      createdAt: NOW - 7 * day, signedAt: NOW - 5 * day,
      accountTypeAtSign: 'company',
      revisionLimitDefault: 2,
      requiresInvoice: true,
    },
    milestones: largeMilestones,
    paymentPlan: largePayments,
    releasePlan: largeReleases,
    changes: [], disputes: [],
  },
];

// "Logged-in" perspective for the demo. UI has a top-bar toggle to flip
// between the two parties so reviewers can see both sides without logging in/out.
export const DEMO_VIEWER_ID = 'demo_creator_001';
